import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageHeader from "Components/PageHeader";
import { useLocation, useNavigate } from "react-router-dom";
import { SbTabs, SbNotification } from "uiCore";
import { PageTransitionStyled } from "../talentMobility.animations";
import { TEXT } from "../talentMobility.constants";
import {
  getViewPlanningRedeploymentPlans,
  getViewPlanningRedeploymentPlanRoles,
  getViewPlanningWorkforcePlans,
  getViewPlanningWorkforcePlanJobs,
  getViewPlanningPaicRedesignPlans,
  getViewPlanningPaicRedesignPlanRoles,
} from "Api/apiService";
import { VIEW_PLANNING_TAB, PAIC_ROLE_TAB } from "./ViewPlanningV2.constants";
import { buildPaicPlanRolesCsvRows } from "./ViewPlanningV2.utils";
import { PAIC_VERSIONS_PATH } from "../PaicOrgRedesignV2.utils";
import {
  buildRedeploymentRoleColumns,
  buildWorkforceJobColumns,
  PAIC_PLAN_EXTRA_COLUMNS,
} from "./useViewPlanningColumns";
import { TabsBodyStyled } from "./ViewPlanningV2.styles";
import PlansListTable from "./components/PlansListTable";
import PlanDrillDownList from "./components/PlanDrillDownList";
import PlanRoleDetail from "./components/PlanRoleDetail";
import WorkforceJobDetail from "./components/WorkforceJobDetail";
import PaicOrganizationRoles from "./components/PaicOrganizationRoles";

import useAnalytics from "Hooks/useAnalytics";
import { CROSS_PAGE_TOUR_PHASE, getTourLegSteps } from "../TalentMobilityManagerV2/TalentMobilityManagerV2.constants";
import useTourStore from "Store/useTourStore";
import SbTourLeg from "uiCore/SbTour/SbTourLeg";
import { ANALYTICS_EVENTS } from "Utils/analytics";
import { exportRowsAsCsv } from "Utils/exportCsv.utils";



// Planning Tools ▸ View Planning — read-only view over the saved plans.
// Three tabs, each landing on its saved plans list (name, creator, date,
// job count): Redeployment Planning drills plan → roles → the role's
// Reduction Detail mirror; Workforce Planning drills plan → jobs → the
// job's available/unavailable candidates; PAIC Redesign Plan drills plan →
// Role Reduced/Added of its one organization (org redesign version), where a
// role expands in place to its shortlisted employees. This page owns the drill-down
// state and fetches; the components/ files own presentation.

const ANALYTICS_FROM = "talent_mobility_view_planning";
const ANALYTICS_EVENT = ANALYTICS_EVENTS.TALENT_MOBILITY.VIEW_PLANNING_VIEWED;

const ViewPlanningV2 = () => {
  const track = useAnalytics(ANALYTICS_FROM);
  // This screen has no Mixpanel coverage of its own; the automatic
  // pageview only records the URL, not that the module was reached.
  useEffect(() => {
    track(ANALYTICS_EVENT);
  }, [track]);
  const navigate = useNavigate();
  // ── Guided tour: this page's leg of the Talent Mobility tour ───────────────
  const crossPageTourPhase = useTourStore((s) => s.crossPageTourPhase);
  const setCrossPageTourPhase = useTourStore((s) => s.setCrossPageTourPhase);
  const tabsBodyRef = useRef(null);
  const handOffTour = (phase, route) => {
    setCrossPageTourPhase(phase);
    navigate(route);
  };
  const location = useLocation();
  // Seeded from router state (see persistDrillState below) so that
  // navigating away — e.g. into a Candidate Profile via a flow=view-planning
  // "View Profile" link — and back via the browser's Back button remounts
  // this page on the same tab/plan/role/job instead of always resetting to
  // the plans list.
  const [activeTab, setActiveTabRaw] = useState(location.state?.activeTab || VIEW_PLANNING_TAB.REDEPLOYMENT);
  const [plans, setPlans] = useState(null); // null = loading
  const [selectedPlan, setSelectedPlanRaw] = useState(location.state?.selectedPlan || null); // null = plans list view
  const [redeploymentRoles, setRedeploymentRoles] = useState(null); // null = loading
  const [selectedRole, setSelectedRoleRaw] = useState(location.state?.selectedRole || null); // null = plan roles view
  const [workforcePlans, setWorkforcePlans] = useState(null); // null = loading
  const [selectedWorkforcePlan, setSelectedWorkforcePlanRaw] = useState(location.state?.selectedWorkforcePlan || null); // null = plans list view
  const [workforceJobs, setWorkforceJobs] = useState(null); // null = loading
  const [selectedWorkforceJob, setSelectedWorkforceJobRaw] = useState(location.state?.selectedWorkforceJob || null); // null = plan jobs view
  const [paicPlans, setPaicPlans] = useState(null); // null = loading
  const [selectedPaicPlan, setSelectedPaicPlanRaw] = useState(location.state?.selectedPaicPlan || null); // null = plans list view
  const [paicRoles, setPaicRoles] = useState(null); // null = loading
  const [paicRoleTab, setPaicRoleTabRaw] = useState(location.state?.paicRoleTab || PAIC_ROLE_TAB.REDUCED);
  // The plan's version (or the plan) is gone since the list loaded (404).
  const [isPaicOrganizationGone, setIsPaicOrganizationGone] = useState(false);

  // Replaces this page's own history entry with the drill-down state it's
  // moving to, not just component state. Component state alone doesn't
  // survive navigating away and back (React Router remounts this page fresh
  // on that route), but the history entry's state does — navigate(-1) from a
  // page this component pushed restores it here on mount, above. `patch`
  // overrides only the field(s) this call is changing; every other field
  // falls back to its current (already up to date) value.
  const persistDrillState = (patch) => {
    navigate(location.pathname, {
      replace: true,
      state: {
        activeTab,
        selectedPlan,
        selectedRole,
        selectedWorkforcePlan,
        selectedWorkforceJob,
        selectedPaicPlan,
        paicRoleTab,
        ...patch,
      },
    });
  };

  const setActiveTab = (nextTab) => {
    setActiveTabRaw(nextTab);
    persistDrillState({ activeTab: nextTab });
  };
  const setSelectedPlan = (nextPlan) => {
    setSelectedPlanRaw(nextPlan);
    persistDrillState({ selectedPlan: nextPlan });
  };
  const setSelectedRole = (nextRole) => {
    setSelectedRoleRaw(nextRole);
    persistDrillState({ selectedRole: nextRole });
  };
  const setSelectedWorkforcePlan = (nextPlan) => {
    setSelectedWorkforcePlanRaw(nextPlan);
    persistDrillState({ selectedWorkforcePlan: nextPlan });
  };
  const setSelectedWorkforceJob = (nextJob) => {
    setSelectedWorkforceJobRaw(nextJob);
    persistDrillState({ selectedWorkforceJob: nextJob });
  };
  const setSelectedPaicPlan = (nextPlan) => {
    setSelectedPaicPlanRaw(nextPlan);
    persistDrillState({ selectedPaicPlan: nextPlan });
  };
  const setPaicRoleTab = (nextTab) => {
    setPaicRoleTabRaw(nextTab);
    persistDrillState({ paicRoleTab: nextTab });
  };

  // Fetched when the Redeployment Planning tab is active (it's the default,
  // so also on first load), once per visit.
  useEffect(() => {
    if (activeTab !== VIEW_PLANNING_TAB.REDEPLOYMENT || plans !== null) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await getViewPlanningRedeploymentPlans();
        if (!cancelled) setPlans(Array.isArray(response?.plans) ? response.plans : []);
      } catch (error) {
        if (cancelled) return;
        setPlans([]);
        SbNotification.error(
          "Unable to load redeployment plans",
          error?.response?.data?.message || "Please try again."
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, plans]);

  // The selected plan's role rows, fetched on drill-in.
  useEffect(() => {
    if (!selectedPlan) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await getViewPlanningRedeploymentPlanRoles(selectedPlan.plan_id);
        if (!cancelled) setRedeploymentRoles(Array.isArray(response?.roles) ? response.roles : []);
      } catch (error) {
        if (cancelled) return;
        setRedeploymentRoles([]);
        SbNotification.error(
          "Unable to load redeployment roles",
          error?.response?.data?.message || "Please try again."
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedPlan]);

  // Workforce plans, fetched the first time the Workforce Planning tab opens.
  useEffect(() => {
    if (activeTab !== VIEW_PLANNING_TAB.WORKFORCE || workforcePlans !== null) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await getViewPlanningWorkforcePlans();
        if (!cancelled) setWorkforcePlans(Array.isArray(response?.plans) ? response.plans : []);
      } catch (error) {
        if (cancelled) return;
        setWorkforcePlans([]);
        SbNotification.error(
          "Unable to load workforce plans",
          error?.response?.data?.message || "Please try again."
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, workforcePlans]);

  // The selected workforce plan's job rows, fetched on drill-in.
  useEffect(() => {
    if (!selectedWorkforcePlan) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await getViewPlanningWorkforcePlanJobs(selectedWorkforcePlan.plan_id);
        if (!cancelled) setWorkforceJobs(Array.isArray(response?.jobs) ? response.jobs : []);
      } catch (error) {
        if (cancelled) return;
        setWorkforceJobs([]);
        SbNotification.error(
          "Unable to load workforce plan jobs",
          error?.response?.data?.message || "Please try again."
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedWorkforcePlan]);

  // PAIC redesign plans, fetched the first time the PAIC Redesign Plan tab opens.
  useEffect(() => {
    if (activeTab !== VIEW_PLANNING_TAB.PAIC_REDESIGN || paicPlans !== null) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await getViewPlanningPaicRedesignPlans();
        if (!cancelled) setPaicPlans(Array.isArray(response?.plans) ? response.plans : []);
      } catch (error) {
        if (cancelled) return;
        setPaicPlans([]);
        SbNotification.error(
          "Unable to load PAIC redesign plans",
          error?.response?.data?.message || "Please try again."
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, paicPlans]);

  // A plan covers one organization, so it opens straight on that
  // organization's roles — the plans list row already carries it
  // (`organization`: the plan's oldest-saved visible version; null when none
  // is visible). A plan saved before that rule (several organizations) opens
  // on that first one.
  const activePaicOrganization = selectedPaicPlan?.organization ?? null;

  // The active organization's roles — one fetch feeds both role tabs.
  useEffect(() => {
    if (!selectedPaicPlan || !activePaicOrganization) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await getViewPlanningPaicRedesignPlanRoles(
          selectedPaicPlan.plan_id,
          activePaicOrganization.sb_org_redesign_unique_id
        );
        if (!cancelled) setPaicRoles(Array.isArray(response?.roles) ? response.roles : []);
      } catch (error) {
        if (cancelled) return;
        SbNotification.error(
          "Unable to load organization roles",
          error?.response?.data?.message || "Please try again."
        );
        if (error?.response?.status === 404) {
          // The plan or its version is gone since the list loaded (archived,
          // unpublished, no longer visible): say so instead of empty role
          // tabs that read as "nothing changed", and refresh the list.
          setIsPaicOrganizationGone(true);
          setPaicPlans(null);
          return;
        }
        setPaicRoles([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedPaicPlan, activePaicOrganization]);

  const openPlan = (plan) => {
    setRedeploymentRoles(null);
    setSelectedRoleRaw(null);
    setSelectedPlanRaw(plan);
    persistDrillState({ selectedPlan: plan, selectedRole: null });
  };

  const openWorkforcePlan = (plan) => {
    setWorkforceJobs(null);
    setSelectedWorkforceJobRaw(null);
    setSelectedWorkforcePlanRaw(plan);
    persistDrillState({ selectedWorkforcePlan: plan, selectedWorkforceJob: null });
  };

  // Opening a plan lands on Role Reduced of its organization.
  const openPaicPlan = (plan) => {
    setIsPaicOrganizationGone(false);
    setPaicRoles(null);
    setPaicRoleTabRaw(PAIC_ROLE_TAB.REDUCED);
    setSelectedPaicPlanRaw(plan);
    persistDrillState({ selectedPaicPlan: plan, paicRoleTab: PAIC_ROLE_TAB.REDUCED });
  };

  // Edit column on the Redeployment Planning plans list — jumps into the
  // Redeployment Center immediately, carrying which plan to load as router
  // state; the Center itself fetches that plan's details and shows its own
  // loading state while the fetch is in flight (see RedeploymentCenterV2's
  // loadPlanId effect), rather than making the user wait on this page first.
  // useCallback (here and below): PlansListTable memoizes its columns on onEdit.
  const handleEditPlan = useCallback(
    (plan) => {
      navigate("/talent-mobility/redeployment-center", {
        state: { loadPlanId: plan.plan_id, loadPlanName: plan.plan_name },
      });
    },
    [navigate]
  );

  // Edit column on the Workforce Planning plans list — same "navigate now,
  // load there" handoff as handleEditPlan above, into Workforce Planning
  // instead (see WorkforcePlanningV2's loadPlanId effect).
  const handleEditWorkforcePlan = useCallback(
    (plan) => {
      navigate("/talent-mobility/workforce-planning", {
        state: { loadPlanId: plan.plan_id, loadPlanName: plan.plan_name },
      });
    },
    [navigate]
  );

  // Edit column on the PAIC Redesign Plan plans list — same "navigate now,
  // load there" handoff, straight into the plan's version page (see
  // PaicOrgRedesignV2's loadPlanId effect). A plan with no visible version
  // left can't be edited: opening it anywhere else would let Update attach a
  // different version to it.
  const handleEditPaicPlan = useCallback(
    (plan) => {
      const versionId = plan.organization?.sb_org_redesign_unique_id;
      if (!versionId) {
        SbNotification.info("Unable to edit this plan", "This plan's organization is no longer available.");
        return;
      }
      navigate(`${PAIC_VERSIONS_PATH}/${versionId}`, {
        state: { loadPlanId: plan.plan_id, loadPlanName: plan.plan_name },
      });
    },
    [navigate]
  );

  // Export CSV on the PAIC Redesign Plan plans list — the plan's roles
  // (GET /view-planning/paic-redesign-plan/roles for its version), same
  // columns as the PAIC version page's own export. Tracked per plan, so
  // exports running side by side each keep their own row's spinner (a row's
  // button is disabled while its own export runs).
  const [exportingPaicPlanIds, setExportingPaicPlanIds] = useState([]);
  const handleExportPaicPlan = useCallback(async (plan) => {
    const versionId = plan.organization?.sb_org_redesign_unique_id;
    if (!versionId) {
      SbNotification.info("Nothing to export", "This plan's organization is no longer available.");
      return;
    }
    setExportingPaicPlanIds((planIds) => [...planIds, plan.plan_id]);
    try {
      const response = await getViewPlanningPaicRedesignPlanRoles(plan.plan_id, versionId);
      const rows = buildPaicPlanRolesCsvRows(response?.roles);
      if (!rows.length) {
        SbNotification.info("Nothing to export", "This plan has no shortlisted roles yet.");
        return;
      }
      const fileName = `paic-redesign-plan-${(plan.plan_name || plan.plan_id).replace(/[^\w-]+/g, "-")}`;
      exportRowsAsCsv(rows, fileName);
    } catch (error) {
      SbNotification.error("Unable to export the plan", error?.response?.data?.message || "Please try again.");
    } finally {
      setExportingPaicPlanIds((planIds) => planIds.filter((planId) => planId !== plan.plan_id));
    }
  }, []);

  // Column arrays memoize on their row data — this page re-renders on every
  // drill-down/tab state change, and rebuilding the filter-option lists
  // (Set + sort per column) each time is wasted work that also hands antd a
  // new columns identity.
  const redeploymentRoleColumns = useMemo(
    () => buildRedeploymentRoleColumns(redeploymentRoles || []),
    [redeploymentRoles]
  );
  const workforceJobColumns = useMemo(
    () => buildWorkforceJobColumns(workforceJobs || []),
    [workforceJobs]
  );

  let redeploymentTabContent;
  if (selectedPlan && selectedRole) {
    redeploymentTabContent = (
      <PlanRoleDetail
        selectedPlan={selectedPlan}
        selectedRole={selectedRole}
        onBack={() => setSelectedRole(null)}
      />
    );
  } else if (selectedPlan) {
    redeploymentTabContent = (
      <PlanDrillDownList
        planName={selectedPlan.plan_name}
        columns={redeploymentRoleColumns}
        rows={redeploymentRoles}
        rowKey={(record, index) =>
          record.redeploy_plan_unique_id || `${record.plan_id}-${record.jd_sb_unique_id}` || index
        }
        emptyMessage="No roles in this plan."
        onSelect={setSelectedRole}
        onBack={() => setSelectedPlan(null)}
      />
    );
  } else {
    redeploymentTabContent = (
      <PlansListTable
        plans={plans}
        emptyMessage="No redeployment plans saved yet."
        onOpenPlan={openPlan}
        onEdit={handleEditPlan}
      />
    );
  }

  let workforceTabContent;
  if (selectedWorkforcePlan && selectedWorkforceJob) {
    workforceTabContent = (
      <WorkforceJobDetail
        selectedPlan={selectedWorkforcePlan}
        selectedJob={selectedWorkforceJob}
        onBack={() => setSelectedWorkforceJob(null)}
      />
    );
  } else if (selectedWorkforcePlan) {
    workforceTabContent = (
      <PlanDrillDownList
        planName={selectedWorkforcePlan.plan_name}
        columns={workforceJobColumns}
        rows={workforceJobs}
        rowKey={(record, index) =>
          record.workforce_user_plan_id || `${record.plan_id}-${record.jd_sb_unique_id}` || index
        }
        emptyMessage="No jobs in this plan."
        onSelect={setSelectedWorkforceJob}
        onBack={() => setSelectedWorkforcePlan(null)}
      />
    );
  } else {
    workforceTabContent = (
      <PlansListTable
        plans={workforcePlans}
        emptyMessage="No workforce plans saved yet."
        onOpenPlan={openWorkforcePlan}
        onEdit={handleEditWorkforcePlan}
      />
    );
  }

  let paicTabContent;
  if (selectedPaicPlan) {
    // Straight to the plan's roles — its organization comes with the list row.
    // A plan with no visible organization, or one gone since (404), shows the
    // "no longer available" message instead.
    paicTabContent = (
      <PaicOrganizationRoles
        planId={selectedPaicPlan.plan_id}
        planName={selectedPaicPlan.plan_name}
        organization={activePaicOrganization}
        isOrganizationUnavailable={!activePaicOrganization || isPaicOrganizationGone}
        roles={paicRoles}
        activeTab={paicRoleTab}
        onTabChange={setPaicRoleTab}
        onBack={() => setSelectedPaicPlan(null)}
      />
    );
  } else {
    paicTabContent = (
      <PlansListTable
        plans={paicPlans}
        emptyMessage="No PAIC redesign plans saved yet."
        onOpenPlan={openPaicPlan}
        onEdit={handleEditPaicPlan}
        onExport={handleExportPaicPlan}
        exportingPlanIds={exportingPaicPlanIds}
        extraColumns={PAIC_PLAN_EXTRA_COLUMNS}
      />
    );
  }

  const tabItems = [
    {
      key: VIEW_PLANNING_TAB.REDEPLOYMENT,
      label: "Redeployment Planning",
      children: redeploymentTabContent,
    },
    {
      key: VIEW_PLANNING_TAB.WORKFORCE,
      label: "Workforce Planning",
      children: workforceTabContent,
    },
    {
      key: VIEW_PLANNING_TAB.PAIC_REDESIGN,
      label: "PAIC Redesign Plan",
      children: paicTabContent,
    },
  ];

  return (
    <PageTransitionStyled>
      <PageHeader
        title={TEXT.BTN_VIEW_PLANNING}
        onBack={() => navigate("/talent-mobility")}
        backLabel="Back to Talent Mobility"
      />

      <TabsBodyStyled ref={tabsBodyRef}>
        <SbTabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </TabsBodyStyled>

      {/* This page's leg of the Talent Mobility tour */}
      <SbTourLeg
        active={crossPageTourPhase === CROSS_PAGE_TOUR_PHASE.VIEW_PLANS}
        // Spotlights only the tab row, which renders straight away — each tab
        // fetches its own plans lazily, so waiting on them would never settle.
        ready
        steps={getTourLegSteps(CROSS_PAGE_TOUR_PHASE.VIEW_PLANS)}
        resolveTarget={() => tabsBodyRef.current?.querySelector(".ant-tabs-nav") ?? tabsBodyRef.current}
        onNext={() => handOffTour(CROSS_PAGE_TOUR_PHASE.FINISH, "/talent-mobility")}
        onClose={() => handOffTour(CROSS_PAGE_TOUR_PHASE.FINISH, "/talent-mobility")}
      />
    </PageTransitionStyled>
  );
};

export default ViewPlanningV2;
